"use client";

import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import {
  Children,
  cloneElement,
  Fragment,
  isValidElement,
  type ReactNode,
} from "react";
import { cn } from "../../lib/utils";
import { useLink } from "./use-link";

export {
  ChangingButton,
  type ChangingButtonAction,
  type ChangingButtonProps,
} from "./changing-button";
export { type LinkProps, LinkProvider } from "./link-provider";
export { useLink } from "./use-link";

export const buttonVariants = cva(
  "inline-flex shrink-0 cursor-pointer items-center justify-center whitespace-nowrap font-medium outline-none transition-all focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_.button-loader]:size-4 [&_.button-loader]:shrink-0 [&_.button-loader]:animate-spin",
  {
    variants: {
      mode: {
        B2B: null,
        D2C: "font-brand font-normal shadow-none [&_svg:not(.button-loader)]:size-5 [&_svg:not(.button-loader)]:shrink-0",
      },
      unstyled: {
        true: "bg-transparent font-normal shadow-none hover:bg-transparent hover:shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 disabled:pointer-events-auto disabled:opacity-100",
      },
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-[0_1px_2px_0_rgba(0,0,0,0.06),0_1px_3px_0_rgba(0,0,0,0.1)] hover:bg-primary/90 focus-visible:ring-primary/50",
        secondary:
          "bg-secondary text-secondary-foreground shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] hover:bg-secondary/80 focus-visible:ring-secondary-foreground/20",
        destructive:
          "bg-destructive text-destructive-foreground shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] hover:bg-destructive/90 focus-visible:ring-destructive/50",
        outline:
          "border border-border bg-background text-foreground shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] hover:border-foreground/50 hover:bg-secondary/70 hover:shadow-[0_2px_4px_0_rgba(0,0,0,0.05)] focus-visible:ring-foreground/20",
        "outline-no-hover":
          "border border-border bg-background text-foreground shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] focus-visible:ring-foreground/20",
        ghost:
          "text-foreground hover:bg-secondary focus-visible:ring-foreground/20",
        link: "text-foreground hover:underline focus-visible:ring-foreground/50",
        rounded:
          "rounded-full bg-background font-bold text-foreground shadow-sm",
      },
      size: {
        default: "h-9 gap-2 rounded-md px-4 py-2 text-sm",
        xs: "h-6 gap-1 rounded-md px-3 py-1 text-xs",
        sm: "h-8 gap-2 rounded-md px-4 py-2 text-xs",
        lg: "h-10 gap-2 rounded-md px-4 py-2 text-sm",
        icon: "size-9 rounded-md",
        "icon-sm": "size-8 rounded-md",
        "icon-lg": "size-10 rounded-md",
        "rounded-sm": "h-8 w-8 gap-2 px-4 py-2 text-xs",
        "rounded-lg": "h-12.5 w-12.5 gap-2 px-4 py-2 text-xl",
      },
    },
    compoundVariants: [
      {
        mode: "D2C",
        variant: "default",
        class:
          "bg-primary text-primary-foreground shadow-none hover:bg-primary/90 focus-visible:ring-primary/30 active:bg-blazeorange active:text-white disabled:bg-alabastergrey disabled:text-white disabled:opacity-100",
      },
      {
        mode: "D2C",
        variant: "secondary",
        class:
          "bg-secondary text-secondary-foreground shadow-none hover:bg-secondary/80 focus-visible:ring-secondary-foreground/30 active:bg-blazeorange active:text-white disabled:bg-alabastergrey disabled:text-white disabled:opacity-100",
      },
      {
        mode: "D2C",
        variant: "destructive",
        class:
          "shadow-none focus-visible:ring-current/30 active:bg-blazeorange disabled:bg-alabastergrey disabled:text-white disabled:opacity-100",
      },
      {
        mode: "D2C",
        variant: "outline",
        class:
          "border border-alabastergrey bg-background text-foreground shadow-none hover:border-foreground hover:bg-secondary/70 focus-visible:border-foreground focus-visible:ring-foreground/30 active:border-blazeorange active:bg-background disabled:border-alabastergrey disabled:bg-background disabled:text-alabastergrey disabled:opacity-100",
      },
      {
        mode: "D2C",
        variant: "outline-no-hover",
        class:
          "border border-alabastergrey bg-background text-foreground shadow-none focus-visible:border-foreground focus-visible:ring-foreground/30 active:border-blazeorange disabled:border-alabastergrey disabled:bg-background disabled:text-alabastergrey disabled:opacity-100",
      },
      {
        mode: "D2C",
        variant: "ghost",
        class:
          "bg-transparent text-inherit shadow-none hover:bg-current/10 focus-visible:ring-current/30 active:bg-current/10 active:text-blazeorange disabled:bg-transparent disabled:text-alabastergrey disabled:opacity-100 dark:disabled:text-white dark:disabled:opacity-30",
      },
      {
        mode: "D2C",
        variant: "link",
        class:
          "bg-transparent text-inherit shadow-none focus-visible:ring-current/30 active:text-blazeorange disabled:text-alabastergrey disabled:opacity-100 dark:disabled:text-white dark:disabled:opacity-30",
      },
      {
        mode: "D2C",
        size: "default",
        class: "h-12 gap-2 rounded-sm px-4 py-3 text-base leading-6",
      },
      {
        mode: "D2C",
        size: "xs",
        class: "h-6 gap-1 rounded-sm px-3 py-1 text-xs leading-4",
      },
      {
        mode: "D2C",
        size: "sm",
        class: "h-10 gap-1 rounded-sm px-4 py-3 text-xs leading-4",
      },
      {
        mode: "D2C",
        size: "lg",
        class: "h-12 gap-2 rounded-sm px-4 py-3 text-base leading-6",
      },
      {
        mode: "D2C",
        size: "icon",
        class: "size-10 rounded-sm",
      },
      {
        mode: "D2C",
        size: "icon-sm",
        class: "size-8 rounded-sm",
      },
      {
        mode: "D2C",
        size: "icon-lg",
        class: "size-12 rounded-sm",
      },
      {
        mode: "D2C",
        unstyled: true,
        class:
          "h-auto w-auto gap-0 rounded-none border-0 bg-transparent px-0 py-0 text-inherit shadow-none hover:bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 active:bg-transparent active:text-inherit disabled:bg-transparent disabled:text-inherit",
      },
    ],
    defaultVariants: {
      mode: "B2B",
      variant: "default",
      size: "default",
      unstyled: false,
    },
  }
);

type ButtonBaseProps = VariantProps<typeof buttonVariants> & {
  loading?: boolean;
  className?: string;
  unstyled?: boolean;
};

type ButtonAsButton = ButtonBaseProps &
  Omit<React.ComponentProps<"button">, "className" | "onClick"> & {
    href?: never;
    disabled?: boolean;
    onClick?:
      | ((
          e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent>
        ) => void)
      | React.MouseEventHandler<HTMLButtonElement>;
  };

type ButtonAsLink = ButtonBaseProps &
  Omit<React.ComponentProps<"a">, "className" | "onClick"> & {
    href: string;
    disabled?: boolean;
    onClick?:
      | ((
          e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent>
        ) => void)
      | React.MouseEventHandler<HTMLAnchorElement>;
  };

export type ButtonProps = ButtonAsButton | ButtonAsLink;

function renderButtonContent(children: ReactNode): ReactNode {
  return Children.map(children, (child) => {
    if (typeof child === "string" || typeof child === "number") {
      return <span className="contents">{child}</span>;
    }

    if (
      isValidElement<{ children?: ReactNode }>(child) &&
      child.type === Fragment
    ) {
      return cloneElement(
        child,
        undefined,
        renderButtonContent(child.props.children)
      );
    }

    return child;
  });
}

export function Button(props: ButtonProps): ReactNode {
  const {
    mode,
    variant,
    size,
    unstyled,
    loading = false,
    children,
    href,
    onClick,
    disabled,
    className,
    ...restProps
  } = props;

  const isDisabled = !!disabled || loading;
  const LinkComponent = useLink();

  // If href is provided, render as a link
  if (href) {
    const LinkComp = LinkComponent || "a";
    const linkProps = restProps as React.ComponentProps<"a">;

    // Add security attributes for external links
    const isExternal = href.startsWith("http") || linkProps.target === "_blank";
    const rel =
      linkProps.rel || (isExternal ? "noopener noreferrer" : undefined);

    // Compose onClick to block when disabled/loading
    function handleLinkClick(
      e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
    ) {
      if (isDisabled) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      if (typeof onClick === "function") {
        // onClick accepts both custom and standard handlers
        (
          onClick as (
            e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
          ) => void
        )(e);
      }
    }

    function handleLinkKeyDown(e: React.KeyboardEvent<HTMLAnchorElement>) {
      if (isDisabled && (e.key === " " || e.key === "Enter")) {
        e.preventDefault();
        e.stopPropagation();
      }
    }

    return (
      <LinkComp
        aria-disabled={isDisabled ? "true" : undefined}
        className={cn(
          buttonVariants({ mode, variant, size, unstyled }),
          isDisabled && "disabled:pointer-events-none disabled:opacity-50",
          className
        )}
        href={href}
        onClick={handleLinkClick}
        onKeyDown={handleLinkKeyDown}
        rel={rel}
        tabIndex={isDisabled ? -1 : undefined}
        {...linkProps}
      >
        {loading && (
          <Loader2 aria-hidden="true" className="button-loader animate-spin" />
        )}
        {renderButtonContent(children)}
      </LinkComp>
    );
  }

  const buttonProps = restProps as React.ComponentProps<"button">;

  // Compose onClick handler to properly support both standard and custom onClick types
  function handleButtonClick(
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) {
    if (typeof onClick === "function") {
      // onClick accepts both custom and standard handlers
      (onClick as (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void)(
        e
      );
    }
  }

  return (
    <button
      className={cn(
        buttonVariants({ mode, variant, size, unstyled }),
        className
      )}
      disabled={isDisabled}
      onClick={handleButtonClick}
      {...buttonProps}
    >
      {loading && (
        <Loader2 aria-hidden="true" className="button-loader animate-spin" />
      )}
      {renderButtonContent(children)}
    </button>
  );
}
